import { Component, useCallback, useEffect, useMemo, useRef, useState, type ErrorInfo, type ReactNode } from 'react'
import { AnimatePresence, MotionConfig, motion } from 'motion/react'
import { useLocation } from '@tanstack/react-router'
import { Plus, Sparkles, X } from 'lucide-react'
import type { AIOwnerBrief } from '@/types/ai'
import { useOwnerBrief } from '@/hooks/useOwnerBrief'
import { copilotCapabilities } from '@/lib/aiCapabilities'
import { promptsFor } from '@/lib/aiScreenPrompts'
import { cn } from '@/lib/utils'
import { AssistantRow, BRAND_GRADIENT, CapabilityChips, Orb, PROMPT_ICONS, UserRow } from './chatRows'
import { ChatComposer } from './ChatComposer'
import { useFileDrop } from './attachmentViews'
import { onCopilotCommand, type CopilotCommand } from './copilotBus'
import { errorCode, isHideCode, useCopilotChat, type Capabilities } from './useCopilotChat'

// ── Bhookly AI staff copilot widget ──────────────────────────────────────────
// A floating bubble that opens a chat panel on /admin screens. Mounted once in
// routes/__root.tsx behind useAICopilotGate. The chat itself (conversation,
// turns, ui_actions, confirm cards, voice) lives in useCopilotChat, shared
// with the full page at /admin/ai (BhooklyAIPage.tsx); this file is the
// bubble's layout.
//
// A change the copilot proposes arrives as a confirm card under its reply
// (ActionCard.tsx): nothing changes until the staff member taps Confirm, and
// Undo is offered for the minute after. Not in this slice: the click executor
// for dialog / drawer targets.

export interface BhooklyAIProps {
  role: string
  userId: string
  /** ai_nav_flags from GET /settings. */
  flags: string[]
  /** ai_writes from GET /settings: menu changes are listed among its abilities. */
  writes: boolean
  onHide: () => void
}

const FULL_BRIEF_PROMPT = 'Give me the full brief for yesterday — sales, profit, expenses, late orders, all the detail'

function OwnerBriefCard({
  brief,
  onSeed,
  onDismiss,
}: {
  brief: AIOwnerBrief
  onSeed: (prompt: string) => void
  onDismiss: () => void
}) {
  const suggestions = (brief.metrics?.suggestions ?? []).filter(
    (s) => s && typeof s.label === 'string' && s.label && typeof s.prompt === 'string' && s.prompt,
  )
  return (
    <motion.section
      initial={{ opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      aria-label="Daily brief"
      className="rounded-xl border border-primary/30 bg-primary/5 p-3.5"
    >
      <div className="mb-1.5 flex items-center justify-between">
        <span className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-primary">
          <Sparkles className="h-3.5 w-3.5" aria-hidden /> Daily brief
          {brief.business_date && <span className="font-normal normal-case text-muted-foreground">· {brief.business_date}</span>}
        </span>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss daily brief"
          className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <X className="h-3.5 w-3.5" aria-hidden />
        </button>
      </div>
      <p className="whitespace-pre-wrap break-words text-[13px] leading-relaxed text-foreground">{brief.content}</p>
      <div className="mt-2.5 flex flex-wrap gap-1.5">
        {suggestions.map((s) => (
          <button
            type="button"
            key={s.label}
            onClick={() => onSeed(s.prompt)}
            className="rounded-full border border-primary/40 bg-background px-2.5 py-1 text-[11px] font-medium text-primary transition-colors hover:bg-primary/10"
          >
            {s.label}
          </button>
        ))}
        <button
          type="button"
          onClick={() => onSeed(FULL_BRIEF_PROMPT)}
          className="rounded-full border border-border bg-background px-2.5 py-1 text-[11px] font-medium text-foreground transition-colors hover:bg-muted"
        >
          Full detail
        </button>
      </div>
    </motion.section>
  )
}

// ── Panel ────────────────────────────────────────────────────────────────────

interface PanelProps extends BhooklyAIProps {
  caps: Capabilities
  setCaps: (patch: Partial<Capabilities>) => void
  brief: AIOwnerBrief | null
  onBriefSeen: (id: string) => void
  onClose: () => void
  /** A command from openCopilot (dashboard brief, KPI tiles); `nonce` changes per command. */
  command: (CopilotCommand & { nonce: number }) | null
  /** Called once the command ran, so a reopened panel never replays it. */
  onCommandHandled: () => void
}

function Panel({ role, userId, flags, writes, onHide, caps, setCaps, brief, onBriefSeen, onClose, command, onCommandHandled }: PanelProps) {
  const chat = useCopilotChat({ role, userId, flags, onHide, caps, setCaps })
  const { messages, send, seed, newChat, sending, speech, onFeedback, actOnCard, openCardScreen, pendingLabel, scrollRef, inputRef } =
    chat

  const drop = useFileDrop(caps.files && !chat.reportMode, chat.addFiles)

  useEffect(() => {
    inputRef.current?.focus()
  }, [inputRef])

  // ── Commands from elsewhere in the app (copilotBus) ────────────────────────
  // Seed the composer, or send straight away for safe read questions.
  const handledCommand = useRef<number | null>(null)
  useEffect(() => {
    if (!command || handledCommand.current === command.nonce) return
    handledCommand.current = command.nonce
    if (command.seed && command.send) void send(command.seed)
    else if (command.seed) seed(command.seed)
    onCommandHandled()
  }, [command, send, seed, onCommandHandled])

  // ── Owner brief: chips seed the input, they never send ────────────────────
  const seedFromBrief = useCallback(
    (prompt: string) => {
      if (brief) onBriefSeen(brief.id)
      seed(prompt)
    },
    [brief, onBriefSeen, seed],
  )

  const showBrief = !!brief && !brief.seen
  const empty = messages.length === 0
  // Starters follow the screen: the panel stays open across navigations.
  const { pathname: panelPath } = useLocation()
  const starters = useMemo(() => promptsFor(panelPath, role), [panelPath, role])
  const capabilities = useMemo(() => copilotCapabilities(role, { flags, writes }), [role, flags, writes])

  return (
    <motion.div
      key="panel"
      data-ai-panel=""
      initial={{ opacity: 0, y: 24, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 24, scale: 0.96 }}
      transition={{ type: 'spring', stiffness: 380, damping: 30 }}
      style={{ transformOrigin: 'bottom right' }}
      role="dialog"
      aria-label="Bhookly AI assistant"
      {...drop.props}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.stopPropagation()
          onClose()
        }
      }}
      className="fixed bottom-4 right-4 z-[45] flex h-[min(640px,calc(100dvh-6rem))] w-[400px] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-2xl border border-border bg-background shadow-2xl"
    >
      {/* Header */}
      <div className="flex items-center gap-3 border-b border-border px-4 py-3">
        <Orb size={32} />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold">Bhookly AI</p>
          <p className="truncate text-xs text-muted-foreground">Your POS assistant</p>
        </div>
        {!empty && (
          <button
            type="button"
            onClick={newChat}
            disabled={sending}
            aria-label="Start a new chat"
            className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-40"
          >
            <Plus className="h-4 w-4" aria-hidden />
          </button>
        )}
        <button
          type="button"
          onClick={onClose}
          aria-label="Close Bhookly AI"
          className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <X className="h-4 w-4" aria-hidden />
        </button>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 space-y-5 overflow-y-auto px-4 py-5" aria-live="polite">
        {showBrief && brief && (
          <OwnerBriefCard brief={brief} onSeed={seedFromBrief} onDismiss={() => onBriefSeen(brief.id)} />
        )}
        {empty ? (
          <div className={cn('flex flex-col items-center justify-center px-2 text-center', !showBrief && 'h-full')}>
            <Orb size={52} />
            <h3 className="mt-4 text-base font-semibold">How can I help?</h3>
            <p className="mt-1 text-sm text-muted-foreground">Type or speak, in English or Urdu.</p>
            <div className="mt-4 w-full">
              <CapabilityChips items={capabilities} onPick={seed} disabled={sending} />
            </div>
            <div className="mt-4 grid w-full grid-cols-1 gap-2">
              {starters.map((s) => (
                <button
                  type="button"
                  key={s.text}
                  onClick={() => (s.send ? void send(s.text) : seed(s.text))}
                  disabled={sending}
                  className="flex items-center gap-2.5 rounded-lg border border-border bg-muted/40 px-3 py-2.5 text-left text-sm transition-colors hover:bg-muted disabled:opacity-50"
                >
                  <span className="text-primary">{PROMPT_ICONS[s.icon]}</span>
                  <span className="text-foreground">{s.text}</span>
                </button>
              ))}
            </div>
          </div>
        ) : (
          messages.map((m) =>
            m.role === 'assistant' ? (
              <AssistantRow
                key={m.id}
                message={m}
                pendingLabel={m.pending ? pendingLabel : undefined}
                canSpeak={caps.voiceOut}
                voiceStatus={speech.voiceState?.id === m.id ? speech.voiceState.status : undefined}
                onSpeak={speech.onSpeak}
                onFeedback={onFeedback}
                onCardAction={actOnCard}
                onOpenScreen={openCardScreen}
                onAsk={(text) => void send(text)}
              />
            ) : (
              <UserRow key={m.id} message={m} />
            ),
          )
        )}
      </div>

      <ChatComposer chat={chat} caps={caps} />
      {drop.overlay}
    </motion.div>
  )
}

// ── Bubble ───────────────────────────────────────────────────────────────────

/** The admin dashboard (F-AI-49 renders the brief there): /admin, /admin/ or /admin/dashboard. */
export function isDashboardPath(pathname: string): boolean {
  const p = pathname.replace(/\/+$/, '')
  return p === '/admin' || p === '/admin/dashboard'
}

/** The full Bhookly AI page (F-AI-51): the bubble stays out of its way. */
export function isAIPagePath(pathname: string): boolean {
  return pathname === '/admin/ai' || pathname.startsWith('/admin/ai/')
}

/**
 * The till screens keep their primary actions (Place order, Charge, Fire KOT)
 * in the bottom-right corner, so there the launcher docks as a slim tab on
 * the right edge instead of a corner bubble.
 */
function isTillPath(pathname: string): boolean {
  return ['/admin/counter', '/admin/server'].some((p) => pathname === p || pathname.startsWith(`${p}/`))
}

/** The browser's voice abilities, before the backend has said what it supports. */
export function defaultCapabilities(): Capabilities {
  return {
    voiceIn: typeof window !== 'undefined' && typeof window.MediaRecorder !== 'undefined',
    voiceOut: true,
    report: true,
    files: true,
  }
}

function Copilot(props: BhooklyAIProps) {
  const { role, userId, onHide } = props
  const { pathname } = useLocation()
  const [open, setOpen] = useState(false)
  const launcherRef = useRef<HTMLButtonElement>(null)
  const wasOpen = useRef(false)
  const [caps, setCapsState] = useState<Capabilities>(defaultCapabilities)
  const setCaps = useCallback((patch: Partial<Capabilities>) => setCapsState((c) => ({ ...c, ...patch })), [])

  // The owner brief is shared with the dashboard panel (F-AI-49): one cache,
  // one "seen" state. On the dashboard itself the panel shows it, so the
  // bubble's card stays out of the way there.
  const ownerBrief = useOwnerBrief(userId, role)
  const brief = isDashboardPath(pathname) ? null : ownerBrief.brief
  const markBriefSeen = ownerBrief.markSeen

  useEffect(() => {
    if (isHideCode(errorCode(ownerBrief.error))) onHide()
  }, [ownerBrief.error, onHide])

  // Other screens open the bubble through copilotBus (dashboard brief chips,
  // "ask about today"). The command is handed to the panel once it mounts.
  const [command, setCommand] = useState<(CopilotCommand & { nonce: number }) | null>(null)
  const clearCommand = useCallback(() => setCommand(null), [])
  useEffect(
    () =>
      onCopilotCommand((cmd) => {
        setCommand({ ...cmd, nonce: Date.now() + Math.random() })
        setOpen(true)
      }),
    [],
  )

  // Hand focus back to the launcher when the panel closes.
  useEffect(() => {
    if (wasOpen.current && !open) launcherRef.current?.focus()
    wasOpen.current = open
  }, [open])

  // The page at /admin/ai is the copilot; no bubble on top of it.
  if (isAIPagePath(pathname)) return null

  const hasBrief = !!brief && !brief.seen
  const till = isTillPath(pathname)

  return (
    <>
      <AnimatePresence>
        {open && (
          <Panel
            key="ai-panel"
            {...props}
            caps={caps}
            setCaps={setCaps}
            brief={brief}
            onBriefSeen={markBriefSeen}
            onClose={() => setOpen(false)}
            command={command}
            onCommandHandled={clearCommand}
          />
        )}
      </AnimatePresence>
      {!open && (
        <button
          ref={launcherRef}
          type="button"
          onClick={() => setOpen(true)}
          aria-label={hasBrief ? 'Open Bhookly AI — new daily brief' : 'Open Bhookly AI'}
          aria-haspopup="dialog"
          className={cn(
            'fixed z-[45] flex items-center justify-center text-white shadow-lg transition-transform active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            BRAND_GRADIENT,
            till
              ? 'right-0 top-1/2 h-12 w-8 -translate-y-1/2 rounded-l-xl'
              : 'bottom-5 right-5 h-12 w-12 rounded-full md:h-14 md:w-14',
          )}
        >
          <Sparkles className={till ? 'h-4 w-4' : 'h-6 w-6'} aria-hidden />
          {hasBrief && (
            <span
              className="absolute -right-0.5 -top-0.5 h-3 w-3 rounded-full bg-destructive ring-2 ring-background"
              aria-hidden
            />
          )}
        </button>
      )}
    </>
  )
}

/** A widget failure must never take the till down with it. */
class CopilotErrorBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false }

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true }
  }

  componentDidCatch(error: unknown, info: ErrorInfo): void {
    console.error('[bhookly-ai] widget crashed; hiding it', error, info.componentStack)
  }

  render() {
    return this.state.failed ? null : this.props.children
  }
}

export function BhooklyAI(props: BhooklyAIProps) {
  return (
    <CopilotErrorBoundary>
      <MotionConfig reducedMotion="user">
        <Copilot {...props} />
      </MotionConfig>
    </CopilotErrorBoundary>
  )
}

export default BhooklyAI
